import type {
  BrowsingScope,
  SectionEditability,
  SectionReadOnlyReason,
  ValueScope
} from 'v5/core/form-schema/formSchema.types';

export type ResolveSectionEditabilityInput = {
  section: ValueScope;
  browsing: BrowsingScope;
  /** `accessService.useCan(scope)({ action: RESOURCE_UPDATE })` */
  canUpdateResource: boolean;
  /** Merge modal / previews. */
  forceReadOnly: boolean;
  workspaceArchived: boolean;
};

const EDITABLE: SectionEditability = { type: 'editable' };

function readOnly(reason: SectionReadOnlyReason): SectionEditability {
  return { type: 'read-only', reason };
}

/**
 * Section editability policy (proposal §4.2).
 *
 * Precedence: `no-capability`; then `forceReadOnly` → `display-only` (the
 * resource pane folds a missing capability into its read-only flag); then
 * `archived-workspace`; then the browsing table:
 *
 * | browsing     | Global   | ws:X     | other ws:Y                 |
 * | organization | editable | editable | editable                   |
 * | workspace X  | editable | editable | read-only: other-workspace |
 *
 * There is no per-user workspace access on the backend today; when it
 * exists, only this function changes.
 */
export function resolveSectionEditability(
  input: ResolveSectionEditabilityInput
): SectionEditability {
  if (!input.canUpdateResource) return readOnly('no-capability');
  if (input.forceReadOnly) return readOnly('display-only');
  if (input.workspaceArchived) return readOnly('archived-workspace');

  if (input.browsing.level === 'organization') return EDITABLE;
  if (input.section.type === 'global') return EDITABLE;
  if (input.section.workspaceId === input.browsing.workspaceId) {
    return EDITABLE;
  }
  return readOnly('other-workspace');
}
